import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { architectService } from "../../services/api";

// Async thunks
export const fetchArchitects = createAsyncThunk(
  "architects/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await architectService.getAllArchitects();
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchArchitectById = createAsyncThunk(
  "architects/fetchById",
  async (architectId, { rejectWithValue }) => {
    try {
      const response = await architectService.getArchitectById(architectId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const createArchitect = createAsyncThunk(
  "architects/create",
  async (architectData, { rejectWithValue }) => {
    try {
      const response = await architectService.createArchitect(architectData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateArchitect = createAsyncThunk(
  "architects/update",
  async ({ architectId, architectData }, { rejectWithValue }) => {
    try {
      const response = await architectService.updateArchitect(
        architectId,
        architectData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteArchitect = createAsyncThunk(
  "architects/delete",
  async (architectId, { rejectWithValue }) => {
    try {
      await architectService.deleteArchitect(architectId);
      return architectId;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

const initialState = {
  architects: [],
  currentArchitect: null,
  loading: false,
  error: null,
};

const architectSlice = createSlice({
  name: "architects",
  initialState,
  reducers: {
    clearCurrentArchitect: (state) => {
      state.currentArchitect = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all architects
      .addCase(fetchArchitects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchArchitects.fulfilled, (state, action) => {
        state.loading = false;
        state.architects = action.payload;
      })
      .addCase(fetchArchitects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch architect by ID
      .addCase(fetchArchitectById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchArchitectById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentArchitect = action.payload;
      })
      .addCase(fetchArchitectById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create architect
      .addCase(createArchitect.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createArchitect.fulfilled, (state, action) => {
        state.loading = false;
        state.architects.push(action.payload);
      })
      .addCase(createArchitect.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update architect
      .addCase(updateArchitect.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateArchitect.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.architects.findIndex(
          (a) => a.id === action.payload.id
        );
        if (index !== -1) {
          state.architects[index] = action.payload;
        }
        if (state.currentArchitect?.id === action.payload.id) {
          state.currentArchitect = action.payload;
        }
      })
      .addCase(updateArchitect.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete architect
      .addCase(deleteArchitect.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteArchitect.fulfilled, (state, action) => {
        state.loading = false;
        state.architects = state.architects.filter(
          (a) => a.id !== action.payload
        );
        if (state.currentArchitect?.id === action.payload) {
          state.currentArchitect = null;
        }
      })
      .addCase(deleteArchitect.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearCurrentArchitect, clearError } = architectSlice.actions;
export default architectSlice.reducer;
